import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { Avatar } from '../components/Avatar';
import BackButton from '../components/BackButton';
import { ReminderRow } from '../components/ReminderRow';
import type { LeadComment, LeadDetail as LeadDetailT, LeadReminder } from '../types';

/**
 * Lead detail page. Three blocks: header (lead identity), notes-to-self
 * (private working notes; migrate to ClientComment on conversion), and
 * scheduled reminders (each insert creates a CalendarEvent and queues a
 * push for the user who set it).
 */
export function LeadDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['leads', 'common']);

  const [lead, setLead] = useState<LeadDetailT | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [commentDraft, setCommentDraft] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);

  // Default reminder to "tomorrow at 09:00 local" so the picker isn't a
  // blank slate. ISO is awkward in datetime-local inputs (no offset),
  // so we slice to YYYY-MM-DDTHH:mm.
  const [remindAt, setRemindAt] = useState(() => defaultRemindAt());
  const [reminderNote, setReminderNote] = useState('');
  const [reminderBusy, setReminderBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const d = await apiFetch<LeadDetailT>(`/api/leads/${id}`);
      setLead(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function addComment() {
    const body = commentDraft.trim();
    if (!body || commentBusy) return;
    setCommentBusy(true);
    try {
      const c = await apiFetch<LeadComment>(`/api/leads/${id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      });
      setLead((prev) => (prev ? { ...prev, comments: [c, ...prev.comments] } : prev));
      setCommentDraft('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCommentBusy(false);
    }
  }

  async function deleteComment(cid: string) {
    try {
      await apiFetch(`/api/leads/${id}/comments/${cid}`, { method: 'DELETE' });
      setLead((prev) =>
        prev ? { ...prev, comments: prev.comments.filter((c) => c.id !== cid) } : prev,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function addReminder() {
    if (!remindAt || reminderBusy) return;
    setReminderBusy(true);
    try {
      // datetime-local gives "2026-05-04T09:00" with no zone; serialize
      // as a Date so the server stores the user's wall-clock intent.
      const at = new Date(remindAt);
      const r = await apiFetch<LeadReminder>(`/api/leads/${id}/reminders`, {
        method: 'POST',
        body: JSON.stringify({
          remindAt: at.toISOString(),
          note: reminderNote.trim() || undefined,
        }),
      });
      setLead((prev) =>
        prev
          ? {
              ...prev,
              reminders: [...prev.reminders, r].sort((a, b) =>
                a.remindAt.localeCompare(b.remindAt),
              ),
            }
          : prev,
      );
      setReminderNote('');
      setRemindAt(defaultRemindAt());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setReminderBusy(false);
    }
  }

  async function deleteReminder(rid: string) {
    try {
      await apiFetch(`/api/leads/${id}/reminders/${rid}`, { method: 'DELETE' });
      setLead((prev) =>
        prev ? { ...prev, reminders: prev.reminders.filter((r) => r.id !== rid) } : prev,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function saveReminder(rid: string, next: { remindAt: string; note: string | null }) {
    const updated = await apiFetch<LeadReminder>(`/api/leads/${id}/reminders/${rid}`, {
      method: 'PATCH',
      body: JSON.stringify(next),
    });
    setLead((prev) =>
      prev
        ? {
            ...prev,
            reminders: prev.reminders
              .map((r) => (r.id === rid ? updated : r))
              .sort((a, b) => a.remindAt.localeCompare(b.remindAt)),
          }
        : prev,
    );
  }

  if (loading) return <div className="empty">{t('leads:detail.loading')}</div>;
  if (error || !lead) return <div className="empty">{error ?? t('leads:detail.not_found')}</div>;

  const display = lead.name?.trim() || lead.email || lead.phone || '—';
  const sub = [lead.email, lead.phone, lead.area, t(`leads:source.${lead.source}`)]
    .filter(Boolean)
    .join(' · ');

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <BackButton onClick={() => navigate('/leads')} label={t('leads:detail.back')} />
      </div>

      <div className="full-panel" style={{ marginBottom: 18 }}>
        <div className="panel-header">{t('leads:detail.section_info')}</div>
        <div className="case-row">
          <Avatar name={display} size="md" />
          <div className="case-info">
            <div className="case-name">{display}</div>
            <div className="case-sub">{sub}</div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            {new Date(lead.createdAt).toLocaleDateString(i18n.language, {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </div>
        </div>
      </div>

      <div className="full-panel" style={{ marginBottom: 18 }}>
        <div className="panel-header">{t('leads:detail.section_comments')}</div>
        <div className="form-section">
          <div className="field">
            <textarea
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              placeholder={t('leads:detail.comment_placeholder')}
              rows={3}
              maxLength={5000}
            />
          </div>
          <div style={{ marginTop: 8, textAlign: 'end' }}>
            <button
              className="btn-primary"
              onClick={() => void addComment()}
              disabled={commentBusy || !commentDraft.trim()}
            >
              {t('leads:detail.comment_add')}
            </button>
          </div>
        </div>
        {lead.comments.length === 0 ? (
          <div className="empty">{t('leads:detail.comments_empty')}</div>
        ) : (
          lead.comments.map((c) => (
            <div
              key={c.id}
              className="case-row"
              style={{ alignItems: 'flex-start', gap: 8 }}
            >
              <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
                  {new Date(c.createdAt).toLocaleString(i18n.language, {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
                <div>{c.body}</div>
              </div>
              <button className="btn-cancel" onClick={() => void deleteComment(c.id)}>
                {t('leads:detail.comment_delete')}
              </button>
            </div>
          ))
        )}
      </div>

      <div className="full-panel">
        <div className="panel-header">{t('leads:detail.section_reminders')}</div>
        <div className="form-section">
          <div className="field-grid reminder-add-grid" style={{ gridTemplateColumns: '1fr 2fr auto' }}>
            <div className="field">
              <label>{t('leads:detail.reminder_at')}</label>
              <input
                type="datetime-local"
                value={remindAt}
                onChange={(e) => setRemindAt(e.target.value)}
              />
            </div>
            <div className="field">
              <label>{t('leads:detail.reminder_note')}</label>
              <input
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                placeholder={t('leads:detail.reminder_note_placeholder')}
                maxLength={500}
              />
            </div>
            <div className="field" style={{ alignSelf: 'end' }}>
              <button
                className="btn-primary"
                onClick={() => void addReminder()}
                disabled={reminderBusy || !remindAt}
              >
                {t('leads:detail.reminder_add')}
              </button>
            </div>
          </div>
        </div>
        {lead.reminders.length === 0 ? (
          <div className="empty">{t('leads:detail.reminders_empty')}</div>
        ) : (
          lead.reminders.map((r) => (
            <ReminderRow
              key={r.id}
              reminder={r}
              labels={{
                edit: t('leads:detail.reminder_edit'),
                delete: t('leads:detail.reminder_delete'),
                save: t('leads:detail.reminder_save'),
                editCancel: t('leads:detail.reminder_edit_cancel'),
                notePlaceholder: t('leads:detail.reminder_note_placeholder'),
                pendingPush: t('leads:detail.reminder_pending_push'),
              }}
              onSave={(next) => saveReminder(r.id, next)}
              onDelete={() => deleteReminder(r.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function defaultRemindAt(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  // Format YYYY-MM-DDTHH:mm in local time (no Z) for the datetime-local input.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
